import { PENDING, type ByeKind, type GameResult, type RoundBye } from '@ctd/core';

export interface ResultOption {
  /** Value for the <select>; also accepted by core's parseGameResult. */
  readonly value: string;
  readonly label: string;
  readonly result: GameResult;
}

/** The results offered in the result picker, in menu order. */
export const RESULT_OPTIONS: readonly ResultOption[] = [
  { value: '*', label: '–', result: PENDING },
  { value: '1-0', label: '1-0', result: { white: '1', black: '0' } },
  { value: '1/2-1/2', label: '½-½', result: { white: '=', black: '=' } },
  { value: '0-1', label: '0-1', result: { white: '0', black: '1' } },
  { value: '+-', label: '+ − (white wins by forfeit)', result: { white: '+', black: '-' } },
  { value: '-+', label: '− + (black wins by forfeit)', result: { white: '-', black: '+' } },
  { value: '--', label: '− − (double forfeit)', result: { white: '-', black: '-' } },
  { value: '1-0u', label: '1-0 unrated', result: { white: 'W', black: 'L' } },
  { value: '=u', label: '½-½ unrated', result: { white: 'D', black: 'D' } },
  { value: '0-1u', label: '0-1 unrated', result: { white: 'L', black: 'W' } },
];

export function sameResult(a: GameResult, b: GameResult): boolean {
  return a.white === b.white && a.black === b.black;
}

/** The picker value for a stored result ("" when the pair is not one of the options). */
export function resultValue(r: GameResult): string {
  return RESULT_OPTIONS.find((o) => sameResult(o.result, r))?.value ?? '';
}

export function resultFromValue(value: string): GameResult | null {
  return RESULT_OPTIONS.find((o) => o.value === value)?.result ?? null;
}

/**
 * The result a key sets during fast entry, or null for any other key.
 * 1 white wins, 0 black wins, 5 or = draw, + white wins by forfeit,
 * - black wins by forfeit, Delete or Backspace clears the result.
 */
export function resultForKey(key: string): GameResult | null {
  switch (key) {
    case '1':
      return { white: '1', black: '0' };
    case '0':
      return { white: '0', black: '1' };
    case '5':
    case '=':
      return { white: '=', black: '=' };
    case '+':
      return { white: '+', black: '-' };
    case '-':
      return { white: '-', black: '+' };
    case 'Delete':
    case 'Backspace':
      return PENDING;
    default:
      return null;
  }
}

/** Where focus goes for a navigation key, or null when the key does not move it. */
export function moveForKey(key: string, index: number, count: number): number | null {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowDown':
      return Math.min(index + 1, count - 1);
    case 'ArrowUp':
      return Math.max(index - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

const BYE_LABELS: Record<RoundBye['kind'], string> = {
  U: 'Bye (pairing-allocated)',
  F: 'Full-point bye',
  H: 'Half-point bye',
  Z: 'Zero-point bye',
};

export function byeLabel(kind: RoundBye['kind']): string {
  return BYE_LABELS[kind];
}

export const REQUESTABLE_BYES: readonly { kind: ByeKind; label: string }[] = [
  { kind: 'H', label: 'Half-point bye' },
  { kind: 'Z', label: 'Zero-point bye' },
  { kind: 'F', label: 'Full-point bye' },
];
